import { List, ListDivider } from '@mui/joy';
import { useContext, useEffect } from 'react';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import { AppContext } from '@/shared/context/context';
import { replaceUrlParams } from '~/common/utils/utils';
import ROUTES from '../../App/Router/Routes';
import signalRMessages from '@/redux/actions/signalRMessages';
import { trpc } from '@/config/trpc';
import { useUserRole } from '@/queries';

import {
  AccessKeyIcon,
  AdminIcon,
  CalendarIcon,
  CompanyIcon,
  ContactIcon,
  FinanceIcon,
  HandshakeIcon,
  HomeIcon,
  InsurancePolicyIcon,
  MailboxIcon,
  ReportIcon,
  SupportIcon,
  TaskIcon,
  UploadIcon,
} from '@/shared/icons/Icons';
import BuildingSidebarContent from './BuildingSidebarContent';
import CaseSidebarContent from './CaseSidebarContent';
import CollapsibleSidebarItem from './CollapsibleSidebarItem';
import SidebarItem from './SidebarItem';

const CustomListDivider = () => <ListDivider className="my-3 bg-slate-100" />;

const DEBOUNCE_TIME = 2000;

const DEFAULT_TEAM_PERMISSIONS = {
  allowRepairManagement: false,
  allowSyndicManagement: false,
};

const Sidebar = () => {
  const { currentTeam, ui, socket } = useContext(AppContext);
  const { data: incompleteBatchCount, refetch: refetchIncompleteBatchCount } =
    trpc.mail.batch.incompleteBatchCount.useQuery();
  const { isAdmin, isSupervisor } = useUserRole();
  const { t } = useTranslation();

  useEffect(() => {
    const debouncedRefetch = debounce(refetchIncompleteBatchCount, DEBOUNCE_TIME);

    socket?.on(signalRMessages.BatchStatusUpdated, debouncedRefetch);
    socket?.on(signalRMessages.BatchDeleted, debouncedRefetch);
    socket?.on(signalRMessages.BatchCreated, debouncedRefetch);

    return () => {
      debouncedRefetch.cancel();
      socket?.off(signalRMessages.BatchStatusUpdated, debouncedRefetch);
      socket?.off(signalRMessages.BatchDeleted, debouncedRefetch);
      socket?.off(signalRMessages.BatchCreated, debouncedRefetch);
    };
  }, [refetchIncompleteBatchCount, socket]);

  const { allowRepairManagement, allowSyndicManagement } = currentTeam ?? DEFAULT_TEAM_PERMISSIONS;

  return (
    <List
      className={classNames(
        'fixed left-0 top-0 z-sidebar h-screen overflow-y-auto overflow-x-hidden bg-white pt-[90px]',
        {
          'w-[180px]': ui.toggles.mainMenu,
          'w-[60px]': !ui.toggles.mainMenu,
        },
      )}
      size="sm"
    >
      <SidebarItem icon={<HomeIcon />} route={ROUTES.HOME} title={t('dashboard.label')} />
      <CustomListDivider />
      <SidebarItem icon={<MailboxIcon />} route={ROUTES.MAILBOX} title={t('mailbox')} />
      <SidebarItem icon={<CalendarIcon />} route={ROUTES.CALENDAR} title={t('calendar')} />
      <SidebarItem icon={<TaskIcon />} route={ROUTES.TASKS_OVERVIEW} title={t('tasks')} />
      <CustomListDivider />
      <SidebarItem icon={<ContactIcon />} route={ROUTES.CONTACT_DASHBOARD} title={t('contacts')} />
      <SidebarItem
        icon={<CompanyIcon />}
        route={ROUTES.COMPANIES_OVERVIEW}
        title={t('companies')}
      />

      <BuildingSidebarContent />
      {currentTeam?.allowAccessKeyManagement && (
        <SidebarItem
          icon={<AccessKeyIcon />}
          route={ROUTES.ACCESS_KEYS}
          title={t('_accessKeys.title')}
        />
      )}
      <SidebarItem
        icon={<InsurancePolicyIcon />}
        route={ROUTES.POLICY_OVERVIEW}
        title={t('policies')}
      />
      <CustomListDivider />
      <CaseSidebarContent />
      <CustomListDivider />
      {allowSyndicManagement && (
        <>
          <SidebarItem
            icon={<HandshakeIcon />}
            route={ROUTES.MEETINGS_OVERVIEW}
            title={t('meetings')}
          />
          <CustomListDivider />
        </>
      )}
      <SidebarItem icon={<ReportIcon />} route={ROUTES.INSIGHTS} title={t('insights.title')} />
      <CustomListDivider />
      {(allowRepairManagement || allowSyndicManagement) && (
        <>
          <SidebarItem
            badgeContent={incompleteBatchCount}
            badgeTooltip={t('communicationSidebarTooltip', {
              batchesCount: incompleteBatchCount,
            })}
            icon={<UploadIcon />}
            route={ROUTES.BATCH_COMMUNICATION_OVERVIEW}
            title={t('mailings')}
          />
          <CustomListDivider />
        </>
      )}
      {(allowSyndicManagement || allowRepairManagement) && (
        <>
          <CollapsibleSidebarItem icon={<FinanceIcon />} route="#" title={t('finance')}>
            {allowSyndicManagement && (
              <SidebarItem nested route={ROUTES.HOA_INVOICE_MANAGEMENT} title={t('invoices')} />
            )}
            {allowRepairManagement && (
              <SidebarItem
                nested
                prefixMatch
                route={ROUTES.REPAIR_INVOICE_MANAGEMENT}
                title={t('invoices')}
              />
            )}
            {allowSyndicManagement && (
              <SidebarItem nested route={ROUTES.ACCOUNTING_ENVELOPES} title={t('payments')} />
            )}
            {(isSupervisor || isAdmin) && allowRepairManagement && (
              <SidebarItem nested route={ROUTES.REPAIR_DEBTOR_MANAGEMENT} title={t('debtors')} />
            )}
            {allowSyndicManagement && (
              <>
                <SidebarItem nested route={ROUTES.HOA_DEBTOR_MANAGEMENT} title={t('debtors')} />
                <SidebarItem
                  nested
                  route={replaceUrlParams(ROUTES.ACCOUNTING_STATEMENTS, {
                    homeownerAssociationId: '',
                  })}
                  title={t('statements')}
                />
              </>
            )}
          </CollapsibleSidebarItem>
          <CustomListDivider />
        </>
      )}
      <li className="mb-2 lg:mt-auto">
        <List size="sm">
          {allowSyndicManagement && (
            <SidebarItem
              icon={<AdminIcon />}
              route={ROUTES.SYNDIC_MANAGMENT_OVERVIEW}
              title={t('syndicManagement')}
            />
          )}
          {isAdmin && (
            <SidebarItem
              icon={<AdminIcon />}
              route={ROUTES.ADMINISTRATION}
              title={t('administration')}
            />
          )}
          <SidebarItem
            icon={<SupportIcon />}
            route={ROUTES.SUPPORT_OVERVIEW}
            title={t('support')}
          />
        </List>
      </li>
    </List>
  );
};

export default Sidebar;
